// Project portfolio data. All product names, identifiers and metrics are synthetic.
export const CATEGORIES = [
  "All",
  "Root Cause Analysis",
  "Process Improvement",
  "Data Automation",
  "Manufacturing Analytics",
  "IoT",
  "Quality Systems",
  "Digital Transformation",
  "Continuous Improvement",
  "QMS & Technical Documentation",
];

export const PROJECTS = [
  {
    id: "qc-intelligence-platform",
    title: "Manufacturing Product Quality Intelligence Platform",
    flagship: true,
    categories: ["Manufacturing Analytics", "Digital Transformation", "Data Automation", "Continuous Improvement"],
    problem:
      "Manufacturing and Product Quality failure information existed across multiple systems and file formats, making first-pass yield analysis, Pareto trending, instrument assessment, lot analysis and root-cause investigations unnecessarily manual.",
    methodology:
      "A multi-source analytics architecture designed to transform fragmented manufacturing, analytical and Product Quality data into actionable process intelligence — FPY, Pareto, lot analysis, instrument health, RCA and decision support.",
    tech: ["Python", "pandas", "REST APIs", "Data Pipeline", "Dashboards"],
    value:
      "Converts fragmented Product Quality records into a single analytical layer supporting FPY, Pareto, instrument health, lot trending and RCA workflows.",
    tags: ["Automation", "FPY", "Pareto", "Digital CI", "Manufacturing Analytics", "Data Pipeline"],
    products: ["HelixIEF-200", "HelixIEF-400", "QuantSDS", "RapidSDS-X", "NovaFlow-FX"],
    sections: {
      businessProblem:
        "Manufacturing and Product Quality failure information existed across multiple systems and file formats, making first-pass yield analysis, Pareto trending, instrument assessment, lot analysis and root-cause investigations unnecessarily manual.",
      whyItMattered:
        "Every investigation began with hours of manual data retrieval and reconciliation. Slow access to trustworthy failure data delayed CAPA decisions, obscured recurring loss drivers and increased the cost of scrap and repeat testing.",
      dataEvidence:
        "QC run results, analytical run files, instrument metadata, reagent and component lot genealogies, environmental readings and Quality records — each with different schemas, identifiers and time bases.",
      methodology:
        "Mapped each source, defined a canonical run-level data model, then built layered acquisition, validation, engineering and serving stages so any analysis traces back to preserved source records.",
      analysis:
        "First-pass yield and final yield trending, level-1/level-2 Pareto, instrument and lot performance comparison, retest recovery analysis and quantitative distribution analysis across migration time, resolution, noise, pressure error, temperature and current stability.",
      solution:
        "Designed a unified manufacturing analytics architecture that consolidates Product Quality results, analytical run data, instrument metadata, reagent/component information and process metrics into an interactive decision-support environment.",
      technology:
        "Python harvesters, REST APIs, scheduled collection, file parsing, schema validation, deduplication, normalization, mapping, feature extraction and a master analytical layer consumed by interactive dashboards.",
      qualityCompliance:
        "Source records are preserved unmodified; every derived value carries lineage to its origin. Schema validation and exception logs make data handling auditable, and any operational use would follow controlled-change practices.",
      businessImpact:
        "Analyses that previously required manual assembly become on-demand: Pareto ranking, instrument comparison and lot trending are available interactively, shortening the path from failure signal to investigation.",
      lessons:
        "The architecture pattern — acquire, validate, engineer, serve — transfers to any manufacturing environment where evidence is fragmented across systems. Start with the decision the data must support, then design backwards.",
    },
    techDetails: {
      architecture:
        "Sources (Product Quality results, analytical run files, instrument metadata, reagent data, component/lot data, environmental sensors, Quality records) → Data Acquisition (Python harvesters, REST APIs, scheduled collection, file parsing) → Data Quality (schema validation, deduplication, exception handling, traceability, source preservation) → Data Engineering (normalization, mapping, feature extraction, joining, classification) → Master Analytical Layer → Outputs (FPY, Pareto, instrument health, lot trending, RCA, yield analysis, failure detection, decision support).",
      dataModel:
        "Run-level grain: one row per analytical attempt with run identifier, product, instrument, QC stage, attempt type, result, failure mode/submode, reagent and component lots, quantitative metrics, retest linkage and final disposition. Demonstration set: ~4,500 records across five fictional products and ten instruments, Jan 2025 – Aug 2026.",
      logic:
        "Failure classification is preserved from source rather than re-derived; retests link to their initial attempt so first-pass yield and recovery are computed without double counting.",
      validation:
        "Schema validation on ingestion, range and referential checks, deduplication on natural keys, and reconciliation counts between source and master layer.",
      failureHandling:
        "Malformed files quarantine to an exception log with source path and reason; harvesters checkpoint so reruns resume rather than duplicate.",
      scalability:
        "Batch-incremental acquisition means volume growth adds runtime, not redesign. New sources join through the mapping layer without touching downstream analytics.",
      qualityControls:
        "Immutable raw zone, deterministic transformations, and audit-friendly exception outputs keep the pipeline inspectable.",
      lessonsLearned:
        "Data quality gates must run before engineering, not after; validating late turns every downstream chart into a debugging session.",
    },
  },
  {
    id: "yield-migration-rca",
    title: "Manufacturing Yield & Migration-Time RCA",
    flagship: true,
    categories: ["Root Cause Analysis", "Process Improvement", "Continuous Improvement"],
    problem:
      "A recurring high migration-time failure contributed to reduced first-pass yield and avoidable manufacturing scrap on the RapidSDS-X platform.",
    methodology:
      "Structured scientific RCA: define, contain, verify data, measure, stratify, analyze, hypothesize, test, verify root cause, change, confirm effectiveness.",
    tech: ["RCA", "JMP", "Statistics", "Cp/Cpk", "CAPA", "Change Control"],
    value:
      "Connected statistical evidence to a controlled process/specification review, supporting a CAPA/RCA-driven improvement associated with ~45% scrap reduction (professional impact, stated separately from synthetic charts).",
    tags: ["RCA", "Yield", "Statistics", "CAPA", "Change Control", "Continuous Improvement", "Controlled Documentation"],
    products: ["RapidSDS-X"],
    sections: {
      businessProblem:
        "A recurring high migration-time failure contributed to reduced first-pass yield and avoidable manufacturing scrap.",
      whyItMattered:
        "Each occurrence consumed retest capacity, delayed disposition and — when unrecovered — converted directly to scrap. Recurrence signalled a systemic rather than random cause.",
      dataEvidence:
        "Migration-time populations across instruments, reagent lots and time; initial vs retest outcomes; process-capability history; and change-history records.",
      methodology:
        "An eleven-stage investigation: Define → Contain → Verify Data → Measure → Stratify → Analyze → Hypothesize → Test → Verify Root Cause → Change → Effectiveness.",
      analysis:
        "Pareto ranking, histograms and distribution comparison, box plots, 3σ analysis, process-capability concepts (Cp/Cpk), instrument and reagent-lot comparison, time trends, initial-vs-retest behaviour and process/specification alignment review.",
      solution:
        "Historical process-capability and change-history evidence suggested that an upstream process specification had evolved while downstream Product Quality acceptance criteria required reassessment for alignment. A specification/alignment review was progressed through controlled change.",
      technology:
        "Python/pandas for data assembly and JMP-style statistical analysis: capability indices, distribution comparison, trend visualization and stratified summaries.",
      qualityCompliance:
        "The pathway Evidence → Hypothesis → Statistical Assessment → Process/Specification Review → Change Control → Effectiveness Monitoring kept every action traceable and approved before implementation.",
      businessImpact:
        "The professional outcome associated with this body of work is ~45% scrap reduction from CAPA/RCA-driven process improvement — stated as professional impact, separate from the synthetic demonstration charts on this site.",
      lessons:
        "When a failure is periodic and population-shaped rather than spiky, question the reference frame (specifications, setpoints) before blaming the process. Capability thinking turns a recurring failure into an alignment question.",
    },
    techDetails: {
      architecture:
        "Run-level Product Quality data joined to lot genealogy and change-history timeline, then stratified by instrument, reagent lot, component lot, shift and time.",
      dataModel:
        "Migration-time populations per stratum with initial/retest linkage; synthetic demonstration specification window used for capability illustration only.",
      logic:
        "Distinguish common-cause drift from special-cause spikes by comparing distribution shape, not just means; a shifted-but-tight distribution implicates the reference frame.",
      validation:
        "Confirmed the failure signal was real (not an integration or parsing artifact) before any statistical comparison.",
      failureHandling: "Retest behaviour separates intermittent from persistent failure modes.",
      scalability: "The stratify-then-compare pattern applies to any quantitative Product Quality attribute.",
      qualityControls: "All conclusions gated by converging evidence and routed through change control.",
      lessonsLearned: "Correlation between lot and shift was a confound until time-alignment was checked.",
    },
  },
  {
    id: "microbubble-signal-rca",
    title: "Microbubble & Signal Anomaly Investigation",
    flagship: false,
    categories: ["Root Cause Analysis", "Process Improvement", "Continuous Improvement"],
    problem:
      "Intermittent analytical signal anomalies occurred in Product Quality operations on the NovaFlow-FX platform, requiring determination of whether the behaviour originated from material, fluid handling, equipment, analytical measurement or process conditions.",
    methodology:
      "Observed failure → failure-mode stratification → QC stage comparison → signal extraction → process observation → material/fluid-path investigation → hypothesis → controlled retesting → containment → root-cause assessment.",
    tech: ["RCA", "Fishbone", "5 Whys", "Signal Analysis", "Controlled Retest"],
    value:
      "Separated equipment, material and fluid-path hypotheses with converging evidence, enabling targeted containment instead of broad, costly restrictions.",
    tags: ["RCA", "Process Improvement", "Failure Reduction", "Manufacturing", "Fishbone"],
    products: ["NovaFlow-FX"],
    sections: {
      businessProblem:
        "Intermittent analytical signal anomalies occurred during Product Quality testing, requiring determination of whether the behaviour originated from material, fluid handling, equipment, analytical measurement or process conditions.",
      whyItMattered:
        "Intermittent failures erode confidence in results and can trigger broad containment. Correctly localizing the source determines whether the fix is material, maintenance, method or process.",
      dataEvidence:
        "Run files with extracted signal features, QC stage distribution, instrument assignment, component-lot genealogy and direct process observations.",
      methodology:
        "Direct observation, comparative testing, signal analysis and failure-mode stratification structured by Fishbone and 5 Whys, followed by controlled retesting and process elimination.",
      analysis:
        "Signal feature extraction (peak position, height, width, prominence, artifact signatures), equipment comparison, lot comparison and stage-by-stage occurrence rates.",
      solution:
        "Hypotheses were ranked and tested sequentially; controlled retests distinguished persistent from transient signatures and isolated the contributing condition, informing containment and CAPA thinking.",
      technology:
        "Automated signal-feature extraction over run-file populations, stratified summaries and controlled-retest design.",
      qualityCompliance:
        "Containment actions were proportionate to evidence strength; documentation preserved the hypothesis trail so conclusions remain reconstructable.",
      businessImpact:
        "Investigation scope narrowed from 'anything in the process' to a testable shortlist, reducing repeat testing and avoiding unnecessary broad holds.",
      lessons:
        "Intermittency is information: a failure that appears and disappears under controlled repetition points toward state-dependent causes such as fluid path or handling, not fixed equipment faults.",
    },
  },
  {
    id: "peak-identification-rca",
    title: "Analytical Peak Identification & Specification RCA",
    flagship: false,
    categories: ["Root Cause Analysis", "Manufacturing Analytics"],
    problem:
      "Reference peak position behaviour on the HelixIEF-200 platform required assessment against environmental, electrical, instrumental and reagent factors to determine whether observed variation threatened specification conformance.",
    methodology:
      "Multi-factor evidence build: scatter and correlation screening, distribution comparison, stratification by designation, temperature/current/instrument/lot analysis and specification-window assessment.",
    tech: ["RCA", "Statistics", "Correlation", "p-value", "Specification Review"],
    value:
      "Demonstrates disciplined evidence grading — correlation is not automatically treated as causation — with hypothesis strength updated as each factor is tested.",
    tags: ["RCA", "Statistics", "Specification", "GMP", "Manufacturing"],
    products: ["HelixIEF-200"],
    sections: {
      businessProblem:
        "Reference peak position behaviour required assessment to determine whether observed variation was random, factor-linked or specification-relevant.",
      whyItMattered:
        "Peak identification underpins result validity; drift against a specification window can invalidate otherwise sound runs and mask emerging process movement.",
      dataEvidence:
        "Synthetic reference peak position, temperature, electrical current, instrument, reagent family/designation, lot, run timestamp, signal height, width and prominence.",
      methodology:
        "Screen associations broadly, then deepen only where signal survives stratification; grade evidence strength explicitly at each step.",
      analysis:
        "Scatter plots, correlation with p-values where assumptions are appropriate, distribution comparison, designation stratification, temperature/current/instrument/lot analyses, time trend and specification-window assessment.",
      solution:
        "Evidence separated plausible contributors from coincidences; findings framed a specification-window review question rather than premature blame assignment.",
      technology:
        "Python/pandas feature assembly with statistical testing and layered stratified visualization.",
      qualityCompliance:
        "Good scientific practice is explicit: association alone does not establish causality; sensor location, exposure and time alignment must be verified before concluding environmental root cause.",
      businessImpact:
        "A defensible, evidence-graded answer replaced opinion, focusing the specification review on the factors that survived testing.",
      lessons:
        "Publish the strength of evidence alongside the conclusion. Teams act faster when they can see which hypotheses were eliminated and why.",
    },
  },
  {
    id: "data-harvester",
    title: "Multi-Source Manufacturing Data Harvester",
    flagship: false,
    categories: ["Data Automation", "Digital Transformation", "Continuous Improvement"],
    problem:
      "Manufacturing and Product Quality information distributed across multiple file stores, application endpoints and operational systems required repetitive manual retrieval before investigations could begin.",
    methodology:
      "Automated harvesting workflows that collect, validate, normalize and reconcile manufacturing data into analysis-ready datasets.",
    tech: ["Python", "pandas", "REST API", "CSV", "JSON", "ETL"],
    value:
      "Removes hours of repetitive retrieval per investigation and produces consistent, traceable, analysis-ready datasets on schedule.",
    tags: ["Process Automation", "Cycle-Time Reduction", "Data Quality", "Digital Transformation", "Data Pipeline"],
    sections: {
      businessProblem:
        "Manufacturing and Product Quality information distributed across multiple file stores, application endpoints and operational systems required repetitive manual retrieval before investigations could begin.",
      whyItMattered:
        "Investigator time was spent assembling data instead of analyzing it, and manual assembly introduced inconsistency between investigations.",
      dataEvidence:
        "Heterogeneous sources: CSV/JSON exports, application endpoints and file stores, each with distinct schemas and identifier conventions.",
      methodology:
        "Source A/B/C/D → automated acquisition → validation → normalization → mapping → feature extraction → master dataset → analytics/RCA.",
      analysis:
        "Schema handling, data mapping, deduplication and reconciliation counts verify completeness before analysis consumes the data.",
      solution:
        "Designed automated harvesting workflows that collect, validate, normalize and reconcile manufacturing data into analysis-ready datasets.",
      technology:
        "Python, pandas, REST APIs, CSV/JSON batch and large-file processing, checkpoint/resume, exception logging and source traceability.",
      qualityCompliance:
        "Every harvested record retains source identity and retrieval timestamp; exceptions are logged rather than silently dropped, keeping outputs audit-friendly.",
      businessImpact:
        "Investigations start from a prepared dataset instead of a collection task, compressing the time between problem occurrence and reliable insight.",
      lessons:
        "Checkpoint/resume and deterministic reruns matter more than raw speed; a harvester that can recover safely is one people trust.",
    },
    techDetails: {
      architecture:
        "Per-source connectors behind a common ingestion contract; staging, validation, then conformed master output.",
      dataModel: "Canonical entities with source-system keys preserved for traceability.",
      logic: "Natural-key deduplication; incremental windows keyed on source timestamps.",
      validation: "Schema validation and record-count reconciliation against source manifests.",
      failureHandling: "Checkpoint/resume with per-source exception logs and quarantine.",
      scalability: "Connectors scale horizontally; new sources add configuration, not core code.",
      qualityControls: "Immutable staging copies and logged transformations.",
      lessonsLearned: "Design for the rerun: idempotency simplifies every failure mode.",
    },
  },
  {
    id: "signal-harvester",
    title: "Automated Analytical Run-File Signal Harvester",
    flagship: false,
    categories: ["Data Automation", "Manufacturing Analytics"],
    problem:
      "Large populations of analytical run files required review for specific signal characteristics and recurring failure signatures.",
    methodology:
      "Automated processing logic that extracts targeted analytical features and produces audit-friendly analytical datasets for RCA.",
    tech: ["Python", "Signal Processing", "Feature Extraction", "Batch Processing"],
    value:
      "Converts thousands of run files from manual review objects into a queryable feature dataset with peaks, thresholds, markers and artifact flags.",
    tags: ["Automation", "Signal Analysis", "Feature Extraction", "RCA", "Manufacturing"],
    sections: {
      businessProblem:
        "Large populations of analytical run files required review for specific signal characteristics and recurring failure signatures.",
      whyItMattered:
        "Manual trace review does not scale and is inconsistent between reviewers; subtle recurring signatures stay invisible without systematic extraction.",
      dataEvidence: "High-volume analytical run files containing time-series detector signal and embedded metadata.",
      methodology:
        "Batch processing with peak detection, signal thresholding, feature and metadata extraction, classification and audit output.",
      analysis:
        "Extracted peak position/height/width/prominence, noise baseline estimates, threshold crossings and candidate artifact detection across full run populations.",
      solution:
        "Built automated processing logic capable of extracting targeted analytical features and producing audit-friendly analytical datasets for RCA.",
      technology:
        "Python batch pipeline with checkpoint/resume, exception handling and structured audit outputs.",
      qualityCompliance:
        "Outputs record detection parameters alongside results so any extracted feature can be regenerated and reviewed.",
      businessImpact:
        "Population-scale signal review becomes feasible, letting investigations query 'all runs with this signature' instead of sampling.",
      lessons:
        "Report detection parameters with results; a feature without its extraction context invites misinterpretation.",
    },
    techDetails: {
      architecture: "File discovery → parse → feature extraction → classify → audit dataset.",
      dataModel: "One feature row per detected peak/artifact with run linkage.",
      logic: "Threshold and prominence-based peak detection with noise-baseline estimation.",
      validation: "Spot-verification against reference traces and extraction-parameter logging.",
      failureHandling: "Unreadable files quarantined with reason; runs resume from checkpoint.",
      scalability: "Embarrassingly parallel across files; volume adds compute, not complexity.",
      qualityControls: "Deterministic re-extraction from preserved raw files.",
      lessonsLearned: "Keep the viewer next to the dataset — reviewers trust what they can see.",
    },
  },
  {
    id: "iot-environmental",
    title: "In-House Environmental IoT & Manufacturing Data Acquisition System",
    flagship: true,
    categories: ["IoT", "Data Automation", "Digital Transformation", "Continuous Improvement"],
    subtitle: "Continuous Temperature & Relative-Humidity Monitoring for Manufacturing Analytics",
    problem:
      "Environmental conditions needed to be collected continuously and reliably so temperature and relative-humidity data could be incorporated into manufacturing investigations and process-trending studies.",
    methodology:
      "Internally developed environmental IoT/data-acquisition workflow using network-connected SensorPush sensing hardware and custom software automation — system integration, API acquisition, reliability engineering and analytics integration (not sensor hardware design).",
    tech: ["Python", "REST API", "IoT", "Time-Series", "Scheduled Automation"],
    value:
      "Continuous, gap-repaired environmental telemetry with data-quality auditing — ready for manufacturing correlation and RCA evidence.",
    tags: ["IoT", "Process Monitoring", "Environmental Trending", "Digital CI", "Data Pipeline"],
    sections: {
      businessProblem:
        "Environmental conditions needed to be collected continuously and reliably so temperature and relative-humidity data could be incorporated into manufacturing investigations and process-trending studies.",
      whyItMattered:
        "Without trustworthy continuous telemetry, environmental hypotheses in investigations are untestable, and gaps silently invalidate time alignment.",
      dataEvidence:
        "Temperature and relative-humidity telemetry from network-connected sensors across manufacturing and Product Quality areas.",
      methodology:
        "Physical layer (sensors) → cloud/connectivity (SensorPush API) → Python IoT harvester → data quality (timestamp validation, deduplication, gap detection/repair) → storage (raw master, mirror, backups, logs, reports) → analytics (manufacturing correlation, trending, RCA evidence).",
      analysis:
        "Telemetry completeness auditing, gap reporting before/after repair, daily distributions, temperature/RH relationships and manufacturing overlay exploration.",
      solution:
        "An internally developed environmental IoT/data acquisition workflow using network-connected SensorPush sensing hardware and custom software automation.",
      technology:
        "Python REST API integration, 10-minute polling, continuous synchronization from last stored reading, outage catch-up, deduplication, bounded repair windows, timeout splitting, single-sensor fallback, daily 7-day data-quality audit, backup/mirror logic and Windows Task Scheduler automation.",
      qualityCompliance:
        "No fabricated interpolation: missing values are never mathematically invented to fake continuity — the system retrieves actual available source records and documents completeness with before/after gap reports.",
      businessImpact:
        "Environmental evidence became available to investigations on demand, with completeness quantified rather than assumed.",
      lessons:
        "Reliability features (gap detection, bounded repair, fallback paths) are the product. A polling script is easy; a trustworthy telemetry record is engineering.",
    },
    techDetails: {
      architecture:
        "Sensors → SensorPush cloud API → Python harvester → data-quality layer → raw master + mirror + backups + logs + reports → analytics.",
      dataModel: "Time-series readings keyed by sensor and timestamp; immutable raw plus curated mirrors.",
      logic: "Authenticate → retrieve sensors → read last valid timestamp → request new readings → validate → deduplicate → persist → detect recent gaps → repair from source → audit report → publish for analytics.",
      validation: "Timestamp validation, sensor identification and daily 7-day completeness audits.",
      failureHandling:
        "Outage catch-up, bounded repair windows, timeout window-splitting with safe retries, and single-sensor fallback when multi-sensor requests fail.",
      scalability: "Polling interval and sensor count are configuration; the reliability logic is sensor-agnostic.",
      qualityControls: "Before/after gap reports, chronological sorting, deduplication, backups and mirror datasets.",
      lessonsLearned: "Design for the outage, not the happy path; recovery discipline is what makes data trustworthy.",
    },
  },
  {
    id: "lot-reconciliation",
    title: "Automated Lot & Source-Record Reconciliation Engine",
    flagship: false,
    categories: ["Data Automation", "Quality Systems"],
    problem:
      "Lot attributes needed to be reconciled against multiple source records while maintaining evidence traceability and detecting unresolved or conflicting mappings.",
    methodology:
      "Manufacturing record → lot identifier → source evidence → mapping engine → conflict detection → evidence table → reconciled master dataset.",
    tech: ["Python", "Data Reconciliation", "Traceability", "Exception Handling"],
    value:
      "Every lot mapping carries its evidence; conflicts surface explicitly instead of hiding inside spreadsheets.",
    tags: ["Data Quality", "Traceability", "GMP", "Automation", "Quality Systems", "Revision Control", "ALCOA+"],
    sections: {
      businessProblem:
        "Lot attributes needed to be reconciled against multiple source records while maintaining evidence traceability and detecting unresolved or conflicting mappings.",
      whyItMattered:
        "Genealogy errors propagate silently into investigations and yield analytics; conflicting mappings discovered late undermine confidence in conclusions.",
      dataEvidence: "Manufacturing records, lot identifiers and multiple source evidence systems with overlapping coverage.",
      methodology:
        "Deterministic mapping with explicit conflict detection and an evidence table linking every reconciled value to its sources.",
      analysis: "Match rates, conflict categories, unresolved queue ageing and repeat-conflict patterns.",
      solution:
        "An engine that reconciles lot attributes against source records, retains evidence per mapping and flags conflicts for review rather than guessing.",
      technology: "Python reconciliation logic with mapping rules, exception queues and repeatable processing.",
      qualityCompliance:
        "Evidence retention is the core design principle: reconciled values remain traceable to source, supporting data-integrity expectations.",
      businessImpact:
        "Genealogy questions that took manual cross-checking resolve by query, and unresolved conflicts become a visible managed queue.",
      lessons:
        "Never auto-resolve a conflict silently; an explicit unresolved state is more valuable than a confident wrong answer.",
    },
  },
];

PROJECTS.push({
  id: "yield-recovery-ci",
  title: "Yield Recovery & Scrap Reduction Program",
  flagship: true,
  categories: ["Continuous Improvement", "Process Improvement"],
  problem:
    "Recurring analytical and process-related failures reduced first-pass yield, increased repeat testing and contributed to avoidable manufacturing scrap on the RapidSDS-X platform.",
  methodology:
    "Ten-stage improvement lifecycle: observe, define the loss, establish baseline, stratify, prioritize, root cause, improve, verify, standardize, control.",
  tech: ["Pareto", "Cp/Cpk", "RCA", "CAPA", "Change Control", "Effectiveness Monitoring"],
  value:
    "A complete continuous-improvement program: quantified baseline, stratified loss drivers, verified root cause, controlled change and sustained effectiveness monitoring.",
  tags: ["Continuous Improvement", "Yield", "Pareto", "CAPA", "Effectiveness Monitoring"],
  component: "ci",
  products: ["RapidSDS-X"],
  sections: {
    businessProblem:
      "Recurring analytical and process-related failures reduced first-pass yield, increased repeat testing and contributed to avoidable manufacturing scrap.",
    whyItMattered:
      "Unrecovered failures convert directly to scrap; every retest consumes capacity. A recurring mode signalled a systemic, addressable loss rather than noise.",
    dataEvidence:
      "Run-level Product Quality records across instruments, reagent lots, component lots and time — the same deterministic synthetic dataset powering the Analytics Lab.",
    methodology:
      "Observe → Define the Loss → Baseline → Stratify → Prioritize → Root Cause → Improve → Verify → Standardize → Control.",
    analysis:
      "Pareto ranking, stratification by instrument/lot/month/stage, distribution and box-plot comparison, 3σ and Cp/Cpk capability concepts against a synthetic demonstration specification, and initial-vs-retest behaviour.",
    solution:
      "A hypothetical controlled improvement package: specification review, process-alignment assessment, work-instruction review, manufacturing communication, controlled implementation, training where applicable, and post-change effectiveness monitoring.",
    technology:
      "Deterministic synthetic analytics: live baseline computation, stratification explorer, capability table and effectiveness-monitoring visualization.",
    qualityCompliance:
      "The improvement pathway runs through CAPA, change control, ECO, SOP/WI review, Quality review and training — improvement without losing control of the process.",
    businessImpact:
      "Modelled before/after on synthetic demonstration metrics (scrap index 100 → ~55, retest rate roughly halved). The separate professional-impact statement — ~45% scrap reduction from CAPA/RCA-driven improvement — is identified explicitly and never mixed with synthetic charts.",
    lessons:
      "Improvement is not sustained because the first runs pass; effectiveness monitoring over 30/60/90 days closes the loop and protects the gain.",
  },
});

export const PROJECT_ROUTES = {
  "qc-intelligence-platform": {
    slug: "/projects/manufacturing-qc-intelligence-platform",
    seoTitle: "Manufacturing Product Quality Intelligence Platform | Poojan Patel",
    description: "Interactive synthetic demonstration of multi-source Product Quality analytics, FPY, Pareto, instrument health and lot trending.",
  },
  "yield-migration-rca": {
    slug: "/projects/yield-migration-time-rca",
    seoTitle: "Manufacturing Yield & Migration-Time RCA | Poojan Patel",
    description: "Scientific RCA case study using Pareto, stratification, distributions, process capability concepts and controlled change.",
  },
  "microbubble-signal-rca": {
    slug: "/projects/microbubble-signal-anomaly-rca",
    seoTitle: "Microbubble & Signal Anomaly Investigation | Poojan Patel",
    description: "Interactive manufacturing investigation combining signal extraction, process observation, comparative testing and containment.",
  },
  "peak-identification-rca": {
    slug: "/projects/analytical-peak-identification-rca",
    seoTitle: "Analytical Peak Identification RCA | Poojan Patel",
    description: "Synthetic multivariable investigation of peak position, temperature, electrical current, reagent designation, instrument and lot.",
  },
  "data-harvester": {
    slug: "/projects/multi-source-manufacturing-data-harvester",
    seoTitle: "Multi-Source Manufacturing Data Harvester | Poojan Patel",
    description: "Architecture case study for automated acquisition, validation, normalization, reconciliation and analysis-ready manufacturing datasets.",
  },
  "signal-harvester": {
    slug: "/projects/analytical-run-file-signal-harvester",
    seoTitle: "Analytical Run-File Signal Harvester | Poojan Patel",
    description: "High-volume signal feature extraction, peak detection, thresholding and audit-output workflow using synthetic analytical files.",
  },
  "iot-environmental": {
    slug: "/projects/environmental-iot-data-acquisition",
    seoTitle: "Environmental IoT & Data Acquisition | Poojan Patel",
    description: "API-driven temperature/RH monitoring with scheduled polling, deduplication, gap detection/repair and manufacturing analytics integration.",
  },
  "lot-reconciliation": {
    slug: "/projects/lot-source-record-reconciliation",
    seoTitle: "Lot & Source-Record Reconciliation | Poojan Patel",
    description: "Traceable synthetic workflow for lot mapping, evidence retention, conflict detection and deterministic reconciliation.",
  },
  "controlled-change-documentation": {
    slug: "/projects/technical-findings-to-controlled-change",
    seoTitle: "Technical Findings to Controlled Change | Poojan Patel",
    description: "How a manufacturing investigation becomes controlled documentation: deviation, RCA, ECO, SOP/WI revision, training and effectiveness monitoring.",
  },
  "yield-recovery-ci": {
    slug: "/projects/yield-recovery-scrap-reduction",
    seoTitle: "Yield Recovery & Scrap Reduction | Poojan Patel",
    description: "Continuous-improvement case study connecting baseline, Pareto, RCA, controlled change and effectiveness monitoring.",
  },
};

export const DELIVERABLES = {
  "qc-intelligence-platform": "Interactive Product Quality analytics environment: FPY, Pareto, instrument health, lot trending, RCA support.",
  "yield-migration-rca": "Verified root cause, specification-alignment review and controlled change with effectiveness monitoring.",
  "microbubble-signal-rca": "Evidence-graded investigation record with targeted, proportionate containment.",
  "peak-identification-rca": "Multivariable analytical investigation dataset and specification-review brief.",
  "data-harvester": "Scheduled data-acquisition pipeline producing validated, analysis-ready master datasets.",
  "signal-harvester": "Batch feature-extraction pipeline with audit-friendly analytical signal dataset.",
  "iot-environmental": "Continuous, gap-repaired environmental time-series dataset with data-quality audit reports.",
  "lot-reconciliation": "Reconciled lot-genealogy master dataset with evidence table and managed exception queue.",
  "yield-recovery-ci": "Modelled before/after improvement program with 30/60/90-day effectiveness monitoring.",
  "controlled-change-documentation": "Controlled SOP/WI revision package with training alignment and effectiveness evidence.",
};

PROJECTS.push({
  id: "controlled-change-documentation",
  title: "Technical Findings to Controlled Change",
  subtitle: "How an investigation becomes an approved, traceable, trainable and sustainable Quality-system change.",
  component: "qms-deep",
  flagship: false,
  categories: ["QMS & Technical Documentation", "Quality Systems"],
  problem:
    "A recurring manufacturing issue required both a technical correction and controlled updates to operating documentation.",
  methodology:
    "Observed issue → deviation/investigation → RCA → corrective action → technical change → ECO/change control → SOP/WI revision → training → implementation → effectiveness monitoring.",
  tech: ["SOP Development", "Work Instructions", "Technical Writing", "ECO", "Deviation", "CAPA", "Change Control", "Validation Documentation", "Document Lifecycle", "Revision Control", "ALCOA+", "GDP"],
  value:
    "Quality-system documentation converts technical findings into repeatable operating control — the correction survives shifts, staff changes and audits.",
  tags: ["SOP Development", "Work Instructions", "Technical Writing", "ECO", "Change Control", "Revision Control"],
  sections: {
    businessProblem:
      "A recurring manufacturing issue required both a technical correction and controlled updates to operating documentation.",
    whyItMattered:
      "A technical fix without controlled documentation decays: the next shift, the next operator or the next audit re-opens the issue. In regulated manufacturing, the change is only real once the controlled documents say so.",
    dataEvidence:
      "Investigation records, failure-mode stratification results, the verified root cause, affected SOP/WI inventory, training records and change-history.",
    methodology:
      "Observed Issue → Deviation / Investigation → RCA → Corrective Action → Technical Change → ECO / Change Control → SOP / WI Revision → Training → Implementation → Effectiveness Monitoring.",
    analysis:
      "Evidence strength graded at each step; affected-document mapping identified every instruction, form and record the change touched, including downstream training implications.",
    solution:
      "A controlled documentation package: revised SOP and Work Instruction content reflecting the verified process change, ECO/change-control record, updated controlled form, and a training-alignment plan — all under revision control.",
    technology:
      "Document-control workflow, revision tables, structured technical writing, and analytics outputs from the investigation as objective evidence attachments.",
    qualityCompliance:
      "GDP/ALCOA+ expectations throughout: attributable authorship, traceable rationale, controlled approval path, and retention of superseded versions for reconstructability.",
    businessImpact:
      "The process change became the way the process actually runs — trained, controlled and auditable — with effectiveness monitoring confirming the issue did not recur.",
    lessons:
      "Write for the person executing the step at 2 a.m. Technical accuracy, clarity and implementability are the same requirement viewed from different seats.",
  },
});

export const projectById = (id) => PROJECTS.find((p) => p.id === id);
